import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Flex, Box, Text } from 'rebass/styled-components';
import styled from 'styled-components';
import theme from 'styled-theming';
import { InputField, Button, ConfirmModal } from '../components';
import botTypes from '../bot-types';
import api from '../api';

const textColor = theme('mode', {
  light: (props) => props.theme.colors.black,
  dark: (props) => props.theme.colors.white,
});

const Form = styled.form`
  display: flex;
  flex-direction: column;
  flex: 1;
  position: relative;
`;

const Scroll = styled(Box)`
  overflow: auto;
  padding: 64px;
`;

const Title = styled(Text)`
  color: ${textColor};
`;

/**
 * Currently only used for add bot for customers who are not using automation lookup
 * v2.
 * @param param0
 * @deprecated
 * @returns
 */
function AddBot(props) {
  const {
    userId,
    brandId,
    addNotification,
    setIsModalActive,
    getAutomations,
    fileReader,
    handleFileUpload,
    setFileReaderLoad,
  } = props;

  const [activeBotType, setActiveBotType] = useState(botTypes[0]);
  const [botName, setBotName] = useState('');
  const [botId, setBotId] = useState('');
  const [credentials, setCredentials] = useState('');
  const [isSavingAutomation, setIsSavingAutomation] = useState(false);
  const isMounted = useRef(true);
  const [confirmModalProps, setConfirmModalProps] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const bot = {
      botType: activeBotType.type,
      botName,
      botId,
      credentials,
      userId,
      brandId,
    };

    if (!isSavingAutomation) {
      try {
        await api.automationLookup.createAutomation(bot);
        if (isMounted.current) {
          if (getAutomations) {
            getAutomations();
          }
          if (addNotification) {
            addNotification('Bot added successfully', 'success');
          }
        }
      } catch (error) {
        if (addNotification && isMounted.current) {
          addNotification(error, 'error');
        }
      }
      if (isMounted.current) {
        setIsSavingAutomation(false);
      }
    }

    if (setIsModalActive && isMounted.current) {
      setIsModalActive(false);
    }
  };

  const handleBotNameChange = (event) => setBotName(event.target.value);

  const handleBotIdChange = (event) => setBotId(event.target.value);

  const handleCredentialsChange = (event) => setCredentials(event.target.value);

  const handleRadioChange = (name) => {
    setActiveBotType(botTypes.find((t) => t.name === name));
    setBotId('');
    setCredentials('');
  };

  const handleDrop = (event) => {
    event.preventDefault();
    fileReader.readAsText(event.dataTransfer.files[0]);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const closeModal = (event, confirm) => {
    event.preventDefault();

    if (isMounted.current) {
      if (confirm) {
        setConfirmModalProps({
          show: true,
          detail: 'Are you sure you want to cancel, this will clear all fields and close the modal',
          onConfirm: () => {
            if (isMounted.current) {
              setConfirmModalProps({
                show: false,
              });
              if (setIsModalActive) {
                setIsModalActive(false);
              }
            }
          },
          onCancel: () => {
            if (isMounted.current) {
              setConfirmModalProps({
                show: false,
              });
            }
          },
        });
      } else if (setIsModalActive) {
        setIsModalActive(false);
      }
    }
  };

  const setOnLoadEnd = () => {
    setFileReaderLoad(
      (data) => {
        if (isMounted.current) {
          setCredentials(data);
        }
      },
      () => {
        if (isMounted.current) {
          addNotification('credentials not found in file', 'error');
        }
      },
    );
  };

  useEffect(() => {
    setOnLoadEnd();
    return () => {
      isMounted.current = false;
    };
  }, []); // eslint-disable-line
  // TODO: Instead of disabling lint, memoize the callback

  return (
    <>
      <Form onSubmit={handleSubmit}>
        <Scroll>
          <Box>
            <Title fontSize={3} mb={3}>
              Add Bot
            </Title>
            <InputField
              name="bot provider"
              value={activeBotType}
              handleChange={handleRadioChange}
              inputType="radioField"
            />
          </Box>
          <Box>
            <InputField
              label="Bot Name"
              name="bot name"
              id="botNameInput"
              hint="This must match a bot user Login Name in Conversation Cloud"
              value={botName}
              handleChange={handleBotNameChange}
            />
            <InputField
              label={activeBotType.idLabel}
              name="bot id"
              id="botIdInput"
              hint={activeBotType.hint}
              value={botId}
              handleChange={handleBotIdChange}
            />
            <InputField
              label="Credentials"
              name="bot credentials"
              id="botCredentialsInput"
              hint={
              activeBotType.inputType === 'inputField'
                ? 'Enter the bot credentials token'
                : 'Choose a bot credentials json file'
            }
              value={credentials}
              handleChange={handleCredentialsChange}
              handleDrop={handleDrop}
              handleDragOver={handleDragOver}
              handleFileUpload={handleFileUpload}
              inputType={activeBotType.inputType}
              fileReader={fileReader}
            />
            <Flex>
              <Button
                data-testid="cancel-button"
                id="cancelButton"
                onClick={(event) => closeModal(event, true)}
                mr={2}
                variant="secondary"
              >
                Cancel
              </Button>
              <Button
                data-testid="save-button"
                id="submitButton"
                type="submit"
                variant="primary"
              >
                Save
              </Button>
            </Flex>
          </Box>
        </Scroll>
      </Form>

      <ConfirmModal {...confirmModalProps} />
    </>
  );
}

AddBot.propTypes = {
  setFileReaderLoad: PropTypes.func.isRequired,
  getAutomations: PropTypes.func.isRequired,
  setIsModalActive: PropTypes.func.isRequired,
  addNotification: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired,
  brandId: PropTypes.string.isRequired,
  fileReader: PropTypes.object.isRequired,
  handleFileUpload: PropTypes.func.isRequired,
};

export default AddBot;
